import { useDisclosure } from '@mantine/hooks'
import {
  Close,
  HorizontalRule,
  KeyboardArrowDown,
  Link as LinkIcon,
  RestartAlt,
} from '@mui/icons-material'
import {
  alpha,
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useCallback } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { getAgentChatSettings } from '~/features/agents/utils'
import AssistantAvatar from '~/features/chatbot/components/assistantAvatar'
import Links from '~/features/chatbot/components/simple/header/links'
import SocialLinks from '~/features/chatbot/components/socialLinks'
import { partializeLinks } from '~/features/chatbot/components/socialLinks/utils'
import useIsEmbeddedChat from '~/features/chatbot/hooks/useIsEmbeddedChat'
import {
  getGuestAgentLookupIdentifiers,
  invalidateGuestAgentLookupQueries,
  getChatbotAccentColor,
  sendCloseChat,
} from '~/features/chatbot/utils'
import EndChatDialog from '~/features/chatbotV2/components/simple/header/endChatDialog'
import NewConversationDialog from '~/features/chatbotV2/components/simple/header/newConversationDialog'
import EElement from '~/features/ui/models'
import { EMessageSpeaker } from '~/models/features'
import { store } from '~/store'
import rem from '~/utils/ui'

type TProps = {
  endConversation: () => boolean
  restartConversation: () => boolean
  isAiBusy: boolean
  hasSessionEnded: boolean
}

// v2-specific clone of features/chatbot/components/simple/header (v1) —
// NOT reused as-is, unlike LinkView's header/intro, because the
// close/minimize/links UI itself is what this ticket changes for v2 only:
//   - Close (X) now opens EndChatDialog instead of hiding immediately —
//     "End conversation" ends the conversation server-side (useChatV2's
//     endConversation, threaded in as a prop from Simple) then closes.
//     "Keep chatting" just dismisses the dialog. "Minimize chat" closes
//     the same way minimize does (see below) without ending the
//     conversation.
//   - Minimize (and the dialog's "Minimize chat") fully hide the widget
//     down to just the bubble icon — sendCloseChat, the parent page's
//     existing "toggle #chatbot-container display" handler — rather than
//     shrinking the header in place. Spec: "it should just be the
//     circle... instead of the circle + the minimized chat on top of it."
//     The iframe is never unmounted either way, so the WebSocket
//     connection and conversation state are untouched regardless of how
//     long it stays hidden — reopening via the bubble shows exactly what
//     was there before, nothing to resume. There's deliberately no
//     "restore" state to reflect here (no icon toggle) — while hidden,
//     this component isn't visible to click anything on anyway; un-hiding
//     only happens via the bubble icon on the parent page.
//   - The Links toggle is now a full-width bar ("Helpful links" + icon)
//     instead of a small floating pill, and both it and the dialog's
//     "End conversation" button use the agent's configured highlight/accent
//     color instead of generic theme tokens.
//   - Close (X) skips the confirmation dialog entirely once
//     hasSessionEnded is already true (e.g. the server's own 10-minute
//     inactivity auto-end already fired) — there's no conversation left to
//     confirm ending, "End conversation" would just re-send an already-
//     redundant SESSION_END, so X just closes directly. Same for a guest
//     who opened the widget but never sent a message — there's nothing to
//     confirm ending, so X closes directly instead of asking.
//   - The RestartAlt ("Start a new conversation") button skips its own
//     confirmation dialog the same way, for the same reason: a guest who
//     hasn't sent a message yet has no conversation to lose, so it calls
//     restartConversation directly instead of asking.
//   - Unlike Close and the message input, RestartAlt stays enabled once
//     hasSessionEnded is true — a guest whose conversation just ended is
//     exactly who'd want this button, and useChatV2's restartConversation
//     already supports being called in that state.
function Header({
  endConversation,
  restartConversation,
  isAiBusy,
  hasSessionEnded,
}: TProps) {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { agentId, legacyChatbotId, friendlyUrl } = useParams()
  const { pathname } = useLocation()

  const [open, { toggle }] = useDisclosure(false)
  const [dialogOpened, { open: openDialog, close: closeDialog }] =
    useDisclosure(false)
  const [
    newConversationDialogOpened,
    { open: openNewConversationDialog, close: closeNewConversationDialog },
  ] = useDisclosure(false)

  const [chatbotAgent, chatbotLayout, hasUserSentMessage] = store((state) => [
    state.chatbot.agent,
    state.chatbot.layout,
    state.chatbot.messages.some((m) => m.speaker === EMessageSpeaker.user),
  ])
  const { chatTitle, chatSubtitle } =
    getAgentChatSettings(chatbotAgent?.chatSettings) || {}
  const { accentColor } = getChatbotAccentColor({
    layout: chatbotLayout,
    selectedTheme: chatbotLayout.selection.theme,
  })

  const [socialLinks, agentLinks] = partializeLinks(
    chatbotAgent?.customLinks ?? [],
  )

  const { isEmbedded, isMinimized } = useIsEmbeddedChat()

  const hasAgentLinks = !isEmpty(agentLinks)
  const hasSocialLinks = !isEmpty(socialLinks)
  const hasLinks = hasAgentLinks || hasSocialLinks
  const showLinksButton = hasLinks && (!isEmbedded || !isMinimized)

  const handleMinimize = () => {
    if (open) toggle()
    sendCloseChat()
  }

  const handleImDone = () => {
    // hasSessionEnded means there's nothing left to end — same bypass the
    // X button's own onClick already uses below (hasSessionEnded ||
    // !hasUserSentMessage) — so go straight to closing without calling
    // endConversation() at all (Cursor-flagged, Low severity: gating
    // unconditionally on endConversation()'s return closed the "already
    // ended" gap it was meant to prevent info-loss on, but opened a new
    // one — endConversation() always returns false once the session has
    // already ended, e.g. an inactivity timeout or another tab ending the
    // chat while this dialog happened to still be open, so "I'm done"
    // silently did nothing and there was no way left to leave through the
    // primary action). Otherwise, only proceed on a confirmed send, same
    // "preserve state, let them retry" pattern as dialogInput's own
    // mutate() gating.
    if (hasSessionEnded || endConversation()) {
      closeDialog()
      sendCloseChat()
    }
  }

  const handleHideForNow = () => {
    closeDialog()
    handleMinimize()
  }

  const invalidateGuestAgentSettings = useCallback(() => {
    invalidateGuestAgentLookupQueries(
      queryClient,
      getGuestAgentLookupIdentifiers({
        agentId,
        legacyChatbotId,
        friendlyUrl,
        pathname,
      }),
    ).catch(() => undefined)
  }, [queryClient, agentId, legacyChatbotId, friendlyUrl, pathname])

  const handleRestartConversation = useCallback(() => {
    invalidateGuestAgentSettings()
    return restartConversation()
  }, [invalidateGuestAgentSettings, restartConversation])

  const handleStartNewConversation = () => {
    // Same reasoning as handleImDone above — restartConversation's own
    // return value tells us whether SESSION_RESTART actually went out (it
    // returns false while a reply/reconnect is already in flight, or if the
    // socket wasn't ready), so only dismiss the dialog when it did.
    // Otherwise the dialog closed regardless, leaving the guest looking at
    // the still-live old thread with no indication the restart didn't
    // happen (Cursor-flagged, Medium severity).
    if (handleRestartConversation()) {
      closeNewConversationDialog()
    }
  }

  return (
    <Stack
      id={EElement.chatbotHeader}
      sx={{
        width: '100%',
        backgroundColor: theme.palette.common.white,
        borderBottom: `${rem(1)} solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        sx={{
          width: '100%',
          padding: rem(12),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            gap: rem(8),
          }}
        >
          <AssistantAvatar chatbotImgURL={chatbotAgent.chatbotImgURL} />
          <Stack
            sx={{
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontSize: rem(18),
                fontWeight: theme.typography.fontWeightMedium,
                lineHeight: 1.4,
              }}
            >
              {chatTitle || chatbotAgent.businessName}
            </Typography>
            {!!chatSubtitle && (
              <Typography
                sx={{
                  fontSize: rem(16),
                }}
              >
                {chatSubtitle}
              </Typography>
            )}
          </Stack>

          <IconButton
            onClick={
              hasUserSentMessage
                ? openNewConversationDialog
                : handleRestartConversation
            }
            // Deliberately NOT gated on hasSessionEnded, unlike the Close
            // button and the message input — restarting is exactly what a
            // guest whose conversation just ended would want to do next,
            // and useChatV2's restartConversation already allows it in that
            // state (see its own doc comment). isAiBusy alone still blocks
            // it while a reply from another tab is in flight.
            disabled={isAiBusy}
            size="small"
            sx={{ color: theme.palette.mainFont }}
          >
            <RestartAlt sx={{ fontSize: rem(16) }} />
          </IconButton>

          {isEmbedded && (
            <>
              <IconButton
                onClick={handleMinimize}
                size="small"
                sx={{ color: theme.palette.mainFont }}
              >
                <HorizontalRule sx={{ fontSize: rem(16) }} />
              </IconButton>
              <IconButton
                onClick={
                  hasSessionEnded || !hasUserSentMessage
                    ? sendCloseChat
                    : openDialog
                }
                size="small"
                sx={{ color: theme.palette.mainFont }}
              >
                <Close sx={{ fontSize: rem(16) }} />
              </IconButton>
            </>
          )}
        </Box>
      </Stack>

      {showLinksButton && (
        <Button
          variant="text"
          onClick={toggle}
          disableRipple
          fullWidth
          sx={{
            width: '100%',
            justifyContent: 'center',
            gap: rem(8),
            padding: `${rem(10)} ${rem(12)}`,
            borderTop: `${rem(1)} solid ${theme.palette.divider}`,
            borderRadius: 0,
            textTransform: 'none',
            fontSize: rem(15),
            fontWeight: theme.typography.fontWeightMedium,
            color: accentColor,
            backgroundColor: theme.palette.common.white,
            '&:hover': {
              backgroundColor: alpha(accentColor, 0.06),
            },
          }}
        >
          <LinkIcon sx={{ fontSize: rem(16), transform: 'rotate(135deg)' }} />
          Helpful links
          <KeyboardArrowDown
            sx={{
              fontSize: rem(18),
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease-in-out',
            }}
          />
        </Button>
      )}

      {hasLinks && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              width: '100%',
              backgroundColor: alpha(theme.palette.tertiary5, 0.3),
              padding: rem(12),
              borderTop: `${rem(1)} solid ${theme.palette.divider}`,
            }}
          >
            <Links links={agentLinks} />
            {hasSocialLinks && <SocialLinks links={socialLinks} />}
          </Stack>
        </Collapse>
      )}

      <EndChatDialog
        open={dialogOpened}
        accentColor={accentColor}
        onImDone={handleImDone}
        onKeepChatting={closeDialog}
        onHideForNow={handleHideForNow}
      />
      <NewConversationDialog
        open={newConversationDialogOpened}
        accentColor={accentColor}
        onConfirm={handleStartNewConversation}
        onCancel={closeNewConversationDialog}
      />
    </Stack>
  )
}

export default Header
